import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Trash2, Layers, Scale } from "lucide-react";
import { toast } from "sonner";

const nfmt = (n, d = 2) => {
  if (n == null || isNaN(n)) return "0";
  return Number(Number(n).toFixed(d)).toLocaleString();
};

// Mode-aware Add Stock for Film (rolls → usable weight) and Label (rolls → labels).
// The warehouse worker enters observable facts (roll counts or a measured gross weight);
// the system derives the inventory quantity from the variant's master data.
export default function RollStockDialog({ open, onOpenChange, component, type, designId = "", initialView = "add", onDone }) {
  const [view, setView] = useState(initialView);
  const [tab, setTab] = useState("full");
  const [fullRolls, setFullRolls] = useState("1");
  const [removeRolls, setRemoveRolls] = useState("1");
  const [pallets, setPallets] = useState("1");
  const [gross, setGross] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyPartial, setBusyPartial] = useState(null);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("unassigned");
  const [reweighId, setReweighId] = useState("");
  const [reweighVal, setReweighVal] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/racks").then(({ data }) => setLocations((data || []).map((r) => ({ id: r.id, name: r.name || r.code })))).catch(() => {});
  }, [open]);

  if (!component || !type) return null;
  const mode = component.inventory_mode_effective || "discrete";
  const isLabel = mode === "label";
  const core = Number(type.roll_core_weight) || 0;
  const grossFull = Number(type.roll_gross_weight) || 0;
  const wunit = type.roll_weight_unit || "lbs";
  const net = Math.max(0, grossFull - core);
  const lpr = Number(type.content_per_stock_unit) || 0;
  const rct = type.roll_content_type;
  const filmLength = mode === "film" && ["ft", "m"].includes(rct) && Number(type.content_per_stock_unit) > 0;
  const rpp = Number(type.units_per_pallet) || (Number(type.rolls_per_case || 0) * Number(type.cases_per_pallet || 0)) || 0;
  const summ = (type.roll_summary || {})[designId || "_"] || { full_rolls: 0, partial_count: 0, physical_rolls: 0, partials: [], total: 0 };
  const designName = designId ? ((type.designs || []).find((d) => d.id === designId)?.name || "") : "";

  const perRoll = isLabel ? lpr : (filmLength ? Number(type.content_per_stock_unit) : net);
  const perRollLabel = isLabel ? "labels" : (filmLength ? rct : wunit);
  const weightRatio = isLabel || filmLength;

  const g = parseFloat(gross);
  const partialDerived = (() => {
    if (!(g > 0)) return null;
    if (grossFull > 0 && g > grossFull * 1.02) return { error: `Exceeds a full roll (${nfmt(grossFull)} ${wunit})` };
    if (weightRatio) {
      const netFull = grossFull - core;
      if (netFull <= 0 || perRoll <= 0) return { error: "Set roll gross/core weight and content per roll first" };
      const raw = g > core ? ((g - core) / netFull) * perRoll : 0;
      return { value: isLabel ? Math.floor(raw) : +raw.toFixed(2), unit: perRollLabel, estimate: true };
    }
    return { value: Math.max(0, +(g - core).toFixed(2)), unit: wunit, estimate: false };
  })();

  const canSubmit = () => {
    if (tab === "full") return (parseFloat(fullRolls) || 0) > 0;
    if (tab === "pallets") return (parseFloat(pallets) || 0) > 0 && rpp > 0;
    return partialDerived && !partialDerived.error;
  };

  const submit = async () => {
    const body = { design_id: designId || "", location_id: location };
    if (tab === "full") body.full_rolls = parseFloat(fullRolls) || 0;
    else if (tab === "pallets") body.pallets = parseFloat(pallets) || 0;
    else body.partial_gross_weight = g;
    setSaving(true);
    try {
      await api.post(`/components/${component.id}/types/${type.id}/roll-stock`, body);
      toast.success("Stock added");
      setGross(""); setFullRolls("1"); setPallets("1");
      await onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removePartial = async (p) => {
    setBusyPartial(p.id);
    try {
      await api.post(`/components/${component.id}/types/${type.id}/roll-adjust`, { design_id: designId || "", location_id: p.location_id || "unassigned", partial_id: p.id });
      await onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyPartial(null);
    }
  };

  const reweigh = async (p) => {
    setBusyPartial(p.id);
    try {
      await api.post(`/components/${component.id}/types/${type.id}/roll-adjust`, { design_id: designId || "", location_id: p.location_id || "unassigned", reweigh_partial_id: p.id, gross_weight: parseFloat(reweighVal) });
      setReweighId(""); setReweighVal("");
      await onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyPartial(null);
    }
  };

  const removeFullRolls = async () => {
    const n = Math.max(1, Math.round(parseFloat(removeRolls) || 1));
    setBusyPartial("full");
    try {
      await api.post(`/components/${component.id}/types/${type.id}/roll-adjust`, { design_id: designId || "", location_id: location, full_rolls_delta: -n });
      toast.success(`Removed ${n} full roll${n === 1 ? "" : "s"}`);
      setRemoveRolls("1");
      await onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyPartial(null);
    }
  };

  const inputCls = "rounded-sm border-slate-300 text-right tabular-nums focus-visible:ring-2 focus-visible:ring-[#002FA7]";
  const TabBtn = ({ v, label, disabled }) => (
    <button type="button" disabled={disabled} onClick={() => setTab(v)} className={`flex-1 rounded-sm px-3 py-2 text-xs font-semibold transition-colors ${tab === v ? "bg-[#002FA7] text-white" : "bg-white text-slate-500 hover:text-slate-800"} ${disabled ? "cursor-not-allowed opacity-40" : ""}`} data-testid={`roll-tab-${v}`}>{label}</button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="roll-stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{view === "remove" ? "Remove Stock" : "Add Stock"} — {component.name} · {type.name}{designName ? ` · ${designName}` : ""}</DialogTitle>
          <DialogDescription>
            {view === "remove"
              ? "Remove full rolls, delete a partial, or re-weigh a partial to correct its remaining stock."
              : isLabel ? "Enter full rolls, pallets, or weigh a partial roll — labels are derived automatically." : `Enter full rolls, pallets, or weigh a partial roll — usable ${filmLength ? "film length" : "film weight"} is derived automatically.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="roll-current-summary">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">On hand</span>
            <span className="tabular-nums font-semibold text-slate-800">{nfmt(summ.total, isLabel ? 0 : 2)} {isLabel ? "labels" : perRollLabel}</span>
          </div>
          <div className="mt-0.5 flex items-center justify-between text-xs text-slate-500">
            <span>{summ.physical_rolls} physical rolls ({summ.full_rolls} full · {summ.partial_count} partial)</span>
            <span className="text-[11px] text-slate-400">1 roll ≈ {nfmt(perRoll, isLabel ? 0 : 2)} {perRollLabel}</span>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2" data-testid="roll-location-row">
          <span className="text-xs text-slate-500">Location</span>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="h-8 flex-1 rounded-sm text-xs" data-testid="roll-location"><span>{location === "unassigned" ? "Unassigned / Receiving" : (locations.find((l) => l.id === location)?.name || location)}</span></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned / Receiving</SelectItem>
              {locations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-2 flex gap-1 rounded-sm border border-slate-200 p-1" data-testid="roll-view-toggle">
          <button type="button" onClick={() => setView("add")} className={`flex-1 rounded-sm px-3 py-2 text-xs font-semibold transition-colors ${view === "add" ? "bg-[#002FA7] text-white" : "bg-white text-slate-500 hover:text-slate-800"}`} data-testid="roll-view-add">Add stock</button>
          <button type="button" onClick={() => setView("remove")} className={`flex-1 rounded-sm px-3 py-2 text-xs font-semibold transition-colors ${view === "remove" ? "bg-[#002FA7] text-white" : "bg-white text-slate-500 hover:text-slate-800"}`} data-testid="roll-view-remove">Remove stock</button>
        </div>

        {view === "add" && (<>
        <div className="mt-2 flex gap-1 rounded-sm border border-slate-200 p-1">
          <TabBtn v="full" label="Full rolls" />
          <TabBtn v="pallets" label="Pallets" disabled={rpp <= 0} />
          <TabBtn v="partial" label="Partial roll (weigh)" />
        </div>

        {tab === "full" && (
          <div className="space-y-2" data-testid="roll-add-full">
            <span className="text-xs text-slate-500">Number of full rolls received</span>
            <Input type="text" inputMode="numeric" value={fullRolls} onChange={(e) => setFullRolls(e.target.value.replace(/[^0-9.]/g, ""))} className={inputCls} data-testid="roll-full-input" />
            <p className="text-[11px] text-slate-500" data-testid="roll-full-preview">Adds <span className="font-semibold text-slate-800">{nfmt((parseFloat(fullRolls) || 0) * perRoll, isLabel ? 0 : 2)} {perRollLabel}</span> ({parseFloat(fullRolls) || 0} rolls × {nfmt(perRoll, isLabel ? 0 : 2)})</p>
          </div>
        )}

        {tab === "pallets" && (
          <div className="space-y-2" data-testid="roll-add-pallets">
            <span className="text-xs text-slate-500">Number of pallets received</span>
            <Input type="text" inputMode="numeric" value={pallets} onChange={(e) => setPallets(e.target.value.replace(/[^0-9.]/g, ""))} className={inputCls} data-testid="roll-pallets-input" />
            <p className="text-[11px] text-slate-500" data-testid="roll-pallets-preview">{rpp > 0 ? <>Adds <span className="font-semibold text-slate-800">{nfmt((parseFloat(pallets) || 0) * rpp * perRoll, isLabel ? 0 : 2)} {perRollLabel}</span> ({parseFloat(pallets) || 0} × {rpp} rolls × {nfmt(perRoll, isLabel ? 0 : 2)})</> : "Set rolls per pallet on this variant to use pallets."}</p>
          </div>
        )}

        {tab === "partial" && (
          <div className="space-y-2" data-testid="roll-add-partial">
            <span className="text-xs text-slate-500">Current gross roll weight ({wunit})</span>
            <Input type="text" inputMode="decimal" value={gross} onChange={(e) => setGross(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={`e.g. ${grossFull ? (grossFull / 2).toFixed(1) : "3.6"}`} className={inputCls} data-testid="roll-partial-input" />
            <p className="text-[11px] text-slate-400">Full roll {nfmt(grossFull)} {wunit} · core {nfmt(core)} {wunit}</p>
            {partialDerived?.error ? (
              <p className="text-[11px] font-semibold text-red-600" data-testid="roll-partial-error">{partialDerived.error}</p>
            ) : partialDerived ? (
              <p className="text-sm" data-testid="roll-partial-preview">Estimated: <span className="font-bold text-[#002FA7]">{partialDerived.estimate ? "≈ " : ""}{nfmt(partialDerived.value, isLabel ? 0 : 2)} {partialDerived.unit}</span></p>
            ) : null}
          </div>
        )}

        <Button className="mt-1 h-10 w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving || !canSubmit()} onClick={submit} data-testid="roll-add-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to stock"}
        </Button>
        </>)}

        {view === "remove" && (<>
        {summ.full_rolls > 0 && (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-sm border border-slate-200 px-2 py-2 text-sm" data-testid="roll-remove-full-box">
            <span className="flex items-center gap-2 text-slate-700"><Layers className="h-3.5 w-3.5 text-slate-400" />{summ.full_rolls} full roll{summ.full_rolls === 1 ? "" : "s"} on hand</span>
            <div className="flex items-center gap-1.5">
              <Input type="text" inputMode="numeric" value={removeRolls} onChange={(e) => setRemoveRolls(e.target.value.replace(/[^0-9]/g, ""))} className="h-8 w-16 rounded-sm border-slate-300 text-right text-xs tabular-nums" data-testid="roll-remove-count-input" />
              <Button type="button" variant="outline" className="h-8 rounded-sm border-red-200 px-3 text-xs text-red-600 hover:bg-red-50" disabled={busyPartial === "full" || !(parseInt(removeRolls) > 0)} onClick={removeFullRolls} data-testid="roll-remove-full-submit">
                {busyPartial === "full" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Remove"}
              </Button>
            </div>
          </div>
        )}
        {summ.full_rolls === 0 && summ.partials.length === 0 && (
          <p className="mt-2 rounded-sm border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400" data-testid="roll-remove-empty">No rolls on hand for this variant{location !== "unassigned" ? " — check the selected location" : ""}.</p>
        )}
        </>)}

        {(view === "remove") && (summ.partials.length > 0 || summ.full_rolls > 0) && (
          <div className="mt-2 border-t border-slate-100 pt-3" data-testid="roll-manage">
            {summ.partials.length > 0 && <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Partial rolls</p>}
            {summ.partials.map((p) => (
              <div key={p.id} className="mt-1 rounded-sm border border-slate-100 px-2 py-1.5 text-sm" data-testid={`roll-partial-row-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-slate-700">Partial: <span className="font-semibold">{p.is_estimate ? "≈ " : ""}{nfmt(p.remaining, isLabel ? 0 : 2)} {isLabel ? "labels" : perRollLabel}</span>{p.last_measured_weight ? <span className="ml-1 text-[11px] text-slate-400">({nfmt(p.last_measured_weight)} {wunit})</span> : null}</span>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => { setReweighId(reweighId === p.id ? "" : p.id); setReweighVal(""); }} className="text-slate-400 hover:text-[#002FA7]" title="Re-weigh this roll" data-testid={`roll-reweigh-${p.id}`}><Scale className="h-3.5 w-3.5" /></button>
                    <button type="button" disabled={busyPartial === p.id} onClick={() => removePartial(p)} className="text-slate-400 hover:text-red-600" title="Remove this partial roll" data-testid={`roll-remove-partial-${p.id}`}>{busyPartial === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>
                  </div>
                </div>
                {reweighId === p.id && (
                  <div className="mt-1.5 flex items-center gap-1.5" data-testid={`roll-reweigh-box-${p.id}`}>
                    <Input type="text" inputMode="decimal" value={reweighVal} onChange={(e) => setReweighVal(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={`New gross wt (${wunit})`} className="h-8 rounded-sm border-slate-300 text-right text-xs" data-testid={`roll-reweigh-input-${p.id}`} />
                    <Button type="button" className="h-8 rounded-sm bg-[#002FA7] px-3 text-xs text-white hover:bg-[#002080]" disabled={busyPartial === p.id || !(parseFloat(reweighVal) > 0)} onClick={() => reweigh(p)} data-testid={`roll-reweigh-submit-${p.id}`}>Update</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
